import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TrainingVideoPlayerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  video: {
    id: string;
    title: string;
    description: string | null;
    video_path: string;
  } | null;
  isCompleted: boolean;
  onConfirmWatched: () => void;
}

export function TrainingVideoPlayer({
  open,
  onOpenChange,
  video,
  isCompleted,
  onConfirmWatched,
}: TrainingVideoPlayerProps) {
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [canConfirm, setCanConfirm] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (open && video) {
      loadVideo();
      setCanConfirm(false);
    } else {
      setVideoUrl(null);
    }
  }, [open, video]);

  const loadVideo = async () => {
    if (!video) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from('training-videos')
        .download(video.video_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      setVideoUrl(url);
    } catch (error) {
      console.error('Error loading video:', error);
      toast.error("Fehler beim Laden des Videos");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoEnded = () => {
    setCanConfirm(true);
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      onConfirmWatched();
      toast.success("Video als gesehen bestätigt!");
      onOpenChange(false);
    } catch (error) {
      console.error('Error confirming:', error);
      toast.error("Fehler beim Bestätigen");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{video?.title}</DialogTitle>
          {video?.description && (
            <DialogDescription>{video.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
              <div className="text-muted-foreground">Video wird geladen...</div>
            </div>
          ) : videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              className="w-full max-h-[60vh] rounded-lg bg-black"
              onEnded={handleVideoEnded}
            />
          ) : (
            <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
              <div className="text-muted-foreground">Video konnte nicht geladen werden</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {isCompleted ? (
              <span className="flex items-center gap-2 text-primary">
                <CheckCircle className="h-4 w-4" />
                Bereits bestätigt
              </span>
            ) : canConfirm ? (
              <span className="text-primary font-medium">
                Video vollständig angesehen - Sie können jetzt bestätigen
              </span>
            ) : (
              <span>Schauen Sie das Video bis zum Ende, um es zu bestätigen</span>
            )}
          </div>

          {!isCompleted && (
            <Button
              onClick={handleConfirm}
              disabled={!canConfirm || confirming}
              className="gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              {confirming ? "Wird bestätigt..." : "Verstanden & Bestätigen"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
